"use client"

import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2Icon, LoaderIcon, MoreVerticalIcon, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"

import Create from "./Create"
import react from "react";
import { deleteVehiclePrice, getVehiclePrices } from "@/rental/services/vehicle_service";
import { getCurrencySymbol } from "@/lib/utils";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { getVehicles } from "@/rental/services/reservation-service";
import Delete from "@/rental/shared/Delete";
import { useUserContext } from "@/context/UserContext";

const VehiclePriceList = () => {
    const [data, setData] = react.useState<any[]>([]);
    const [currency, setCurrency] = react.useState<string>('');
    const [rowSelection, setRowSelection] = react.useState({});
    const [sorting, setSorting] = react.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = react.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = react.useState<VisibilityState>({});
    const [addNewIsOpen, setAddNewIsOpen] = react.useState(false);
    const [vehicles, setVehicles] = react.useState<any[]>([]);
    const [selectedClass, setSelectedClass] = react.useState<any>(null);
    const { decodedToken } = useUserContext();

    const columns: ColumnDef<any>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected() ||
                        (table.getIsSomePageRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "className",
            header: "Vehicle",
            cell: ({ row }: any) => (
                <div className="capitalize">{row.getValue("className")}</div>
            ),
        },
        {
            accessorKey: "price",
            header: `Price (${currency})`,
            cell: ({ row }) => (
                <div className="capitalize">{row.getValue("price")}</div>
            ),
        },
        {
            accessorKey: "startDate",
            header: `Starts on`,
            cell: ({ row }) => (
                <div className="capitalize">{row.getValue("startDate")}</div>
            ),
        },
        {
            accessorKey: "endDate",
            header: `Ends on`,
            cell: ({ row }) => (
                <div className="capitalize">{row.getValue("endDate")}</div>
            ),
        },
        {
            accessorKey: "isActive",
            header: "Status",
            cell: ({ row }) => (
                <Badge
                    variant="outline"
                    className="flex gap-1 px-1.5 text-muted-foreground [&_svg]:size-3 overflow-hidden"
                >
                    {row.original.isActive ? (
                        <CheckCircle2Icon className="text-green-500 dark:text-green-400" />
                    ) : (
                        <LoaderIcon className="text-gray-500 dark:text-gray-400" />
                    )}

                    {row.original.isActive ? "Active" : "Inactive"}
                </Badge>
            ),
        },
        {
            id: "actions"
        }
    ]

    const loadData = async (vehicleId?: number) => {

        let _data: any[];

        if (vehicleId) {
            _data = await getVehiclePrices(vehicleId);
        } else {
            _data = await getVehiclePrices(selectedClass?.id);
        }

        const extendedData = _data.map((item: any) => ({
            ...item,
            price: item.seasonal_rate ? parseFloat(item.seasonal_rate).toFixed(2) : "0.00",
            // find class name from classes
            vehicleName: vehicles.find(c => c.id === item.id)?.name || "Unknown",
            startDate: item.date_start_range ? new Date(item.date_start_range).toLocaleDateString() : "N/A",
            endDate: item.date_end_range ? new Date(item.date_end_range).toLocaleDateString() : "N/A",
            isActive: item.date_start_range && item.date_end_range && new Date(item.date_start_range) <= new Date() && new Date(item.date_end_range) >= new Date(),
            // format start_date and end_date to dd/mm/yyyy
        }));

        setData(extendedData);
    }

    react.useEffect(() => {
        const unsub = async () => {
            if (!decodedToken?.user.id) return;

            const currency = getCurrencySymbol(decodedToken?.permissions?.currency || 'EUR');
            setCurrency(currency);

            setVehicles(await getVehicles());
            await loadData();
        }
        unsub();
    }, [decodedToken?.user.id]);

    const confirmDelete = async (id: any) => {
        try {
            await deleteVehiclePrice(id);
            toast.success("Seasonal rate deleted successfully");
            await loadData();
        }
        catch (error) {
            console.error("Error deleting seasonal rate", error);
            toast.error("Failed to delete seasonal rate");
        }
    }

    const table = useReactTable({
        // @ts-ignore
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    })

    return (
        <div className="w-full">
            <div className="flex items-center justify-between py-4">
                <div className="flex-1">
                    <Select onValueChange={(value) => {
                        const selected = vehicles.find(c => c.id === value);
                        setSelectedClass(selected);
                        loadData(selected?.id);
                    }}>
                        <SelectTrigger className="h-8 w-40">
                            <SelectValue placeholder="Vehicle" />
                        </SelectTrigger>
                        <SelectContent align="end">
                            {vehicles.map((vehicle) => (
                                <SelectItem
                                    key={vehicle.id}
                                    value={vehicle.id}
                                >
                                    {vehicle.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-row">
                    <Button onClick={() => setAddNewIsOpen(true)} className="flex items-center space-x-2">
                        <Plus className="h-4 w-4" />
                        Add Price
                    </Button>
                    <Create newEventDialogOpen={addNewIsOpen} setNewEventDialogOpen={setAddNewIsOpen} categories={vehicles} currency={currency} onSaved={loadData} />
                </div>
            </div>

            <div className="rounded-md border">
                <Table>
                    <TableHeader className="bg-muted sticky top-0 z-10">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        cell.column.id === "actions" ? (
                                            <TableCell key={cell.id}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                                                            size="icon">
                                                            <MoreVerticalIcon />
                                                            <span className="sr-only">Open menu</span>
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-32">
                                                        <Delete id={row.original.id} onConfirmDelete={confirmDelete} />
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>) : (

                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>))
                                    )}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} of{" "}
                    {table.getFilteredRowModel().rows.length} row(s) selected.
                </div>
                <div className="space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default VehiclePriceList;